// FILE: UnitLessonPlanGenerator.js
import React, { useState, useRef } from 'react';
import {
    Box, Typography, TextField, Select, MenuItem, Button,
    Paper, CircularProgress, FormControl, InputLabel, Snackbar, Alert, Divider,
    useTheme, useMediaQuery, Fade, Zoom, Container, Stack, Chip, Grid
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import GradeIcon from '@mui/icons-material/Grade';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import DownloadIcon from '@mui/icons-material/Download';
import GenerateIcon from '@mui/icons-material/AutoFixHigh';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TimelineIcon from '@mui/icons-material/Timeline';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ScienceIcon from '@mui/icons-material/Science';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import PushPinIcon from '@mui/icons-material/PushPin';
// Removed import - using predefined subjects array instead
// import useSubjectsAndUnits from '../hooks/useSubjectsAndUnits';

import html2pdf from 'html2pdf.js';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from "docx";
import handleAIError from '../utils/handleAIError';
import api from '../axios/axiosInstance';
import { SpinningGradelyLoader, FloatingGradelyLoader } from '../loadingIcons/LoadingIcons';

// Predefined arrays for dropdown options
const gradeLevels = ["KG", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
const subjects = [
    "Mathematics",
    "English Language Arts",
    "Science",
    "Social Studies",
    "Physical Education",
    "Art",
    "Music",
    "French",
    "Health Education",
    "Career Education"
];

const UnitLessonPlanGenerator = () => {
    const [selectedSubject, setSelectedSubject] = useState('');
    const [grade, setGrade] = useState('');
    const [topic, setTopic] = useState('');
    const [loading, setLoading] = useState(false);
    const [lessonPlan, setLessonPlan] = useState(null);
    const [error, setError] = useState('');
    const lessonPlanRef = useRef();

    // Removed useSubjectsAndUnits hook - using predefined subjects array instead
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    //Dialogue Box for AI Limit
    const [showLimitDialog, setShowLimitDialog] = useState(false);
    const [alertDialog, setAlertDialog] = useState({ open: false, message: "" });




    const handleGenerate = async () => {
        if (!selectedSubject || !grade || !topic.trim()) {
            setError("Please fill out all fields.");
            return;
        }

        setLoading(true);
        setLessonPlan(null);
        setError('');

        try {
            const res = await api.post(
                `/grades/lesson-plan/generate/`,
                {
                    subject: selectedSubject || '',
                    grade,
                    topic
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            setLessonPlan(res.data.lesson_plan);

        } catch (err) {
            // Match your existing error flow exactly
            const errorResponse = err?.response;
            if (errorResponse?.data) {
                err.response = {
                    status: errorResponse.status,
                    data: errorResponse.data
                };
            }

            handleAIError(err, {
                setErrorMsg: (msg) => setAlertDialog({ open: true, message: msg }),
                setShowLimitDialog
            });

        } finally {
            setLoading(false);
        }
    };

    const generatePDFHTML = (lessonPlanData) => {
        // Theme colors for PDF
        const primaryColor = '#546A2F'; // Theme main color
        const secondaryColor = '#A8B87C'; // Theme secondary color
        const successColor = '#2e7d32'; // Material-UI success green
        const infoColor = '#0288d1'; // Material-UI info blue
        const warningColor = '#ed6c02'; // Material-UI warning orange
        const errorColor = '#d32f2f'; // Material-UI error red
        const greyColor = '#757575'; // Material-UI grey
        const lightGreyColor = '#f5f5f5'; // Light grey background
        const borderColor = '#e0e0e0'; // Light border color

        const html = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Lesson Plan - ${selectedSubject?.subject_name || 'Subject'}</title>
            </head>
            <body style="font-family: 'Times New Roman', serif; margin: 0; padding: 0; line-height: 1.5; color: #212121; font-size: 11pt; background: white;">
                <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid ${primaryColor}; padding-bottom: 15px;">
                    <div style="font-size: 20pt; font-weight: bold; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px; color: ${primaryColor};">Lesson Plan</div>
                    <div style="font-size: 12pt; font-weight: bold; margin-bottom: 5px; color: ${secondaryColor};">${selectedSubject?.subject_name || 'Subject'} | Grade ${grade}</div>
                </div>

                <div style="background: ${lightGreyColor}; border: 2px solid ${primaryColor}; padding: 15px; margin-bottom: 25px; border-radius: 8px;">
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
                        <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid ${borderColor};">
                            <span style="font-weight: bold; color: ${primaryColor}; font-size: 10pt; text-transform: uppercase;">Subject:</span>
                            <span style="font-weight: bold; color: #212121; font-size: 10pt;">${selectedSubject?.subject_name || 'N/A'}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid ${borderColor};">
                            <span style="font-weight: bold; color: ${primaryColor}; font-size: 10pt; text-transform: uppercase;">Grade:</span>
                            <span style="font-weight: bold; color: ${successColor}; font-size: 10pt;">${grade}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid ${borderColor};">
                            <span style="font-weight: bold; color: ${primaryColor}; font-size: 10pt; text-transform: uppercase;">Topic:</span>
                            <span style="font-weight: bold; color: ${secondaryColor}; font-size: 10pt;">${topic}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid ${borderColor};">
                            <span style="font-weight: bold; color: ${primaryColor}; font-size: 10pt; text-transform: uppercase;">Duration:</span>
                            <span style="font-weight: bold; color: ${warningColor}; font-size: 10pt;">${lessonPlanData.time || 'N/A'}</span>
                        </div>
                    </div>
                </div>

                ${Object.entries(lessonPlanData).slice(4).map(([key, value]) => `
                    <div style="margin-bottom: 25px; page-break-inside: avoid;">
                        <div style="font-size: 14pt; font-weight: bold; color: ${primaryColor}; margin-bottom: 12px; padding-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px;">${key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                        <div style="background: white; border: 2px solid ${borderColor}; padding: 15px; margin-bottom: 15px; border-radius: 8px;">
                            ${typeof value === 'string' ?
                `<div style="font-size: 10pt; line-height: 1.4; text-align: justify; margin-bottom: 10px; color: #212121;">${value}</div>` :
                Array.isArray(value) ?
                    `<div>${value.map((item, index) => `<div style="margin-bottom: 8px; padding-left: 25px; position: relative; text-align: justify; font-size: 10pt;"><span style="position: absolute; left: 0; font-weight: bold; color: ${primaryColor}; font-size: 11pt;">${index + 1}. </span><span style="color: #212121;">${item}</span></div>`).join('')}</div>` :
                    typeof value === 'object' && value !== null ?
                        Object.entries(value).map(([subKey, subValue]) => `
                                    <div style="margin-bottom: 15px;">
                                        <div style="font-weight: bold; color: ${secondaryColor}; margin-bottom: 6px; font-size: 10pt; text-transform: uppercase;">${subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</div>
                                        ${Array.isArray(subValue) ?
                                `<div>${subValue.map((item, index) => `<div style="margin-bottom: 8px; padding-left: 25px; position: relative; text-align: justify; font-size: 10pt;"><span style="position: absolute; left: 0; font-weight: bold; color: ${primaryColor}; font-size: 11pt;">${index + 1}. </span><span style="color: #212121;">${item}</span></div>`).join('')}</div>` :
                                `<div style="font-size: 10pt; line-height: 1.4; text-align: justify; margin-bottom: 10px; color: #212121;">${subValue}</div>`
                            }
                                    </div>
                                `).join('') :
                        `<div style="font-size: 10pt; line-height: 1.4; text-align: justify; margin-bottom: 10px; color: #212121;">${String(value)}</div>`
            }
                        </div>
                    </div>
                `).join('')}

            </body>
            </html>
        `;
        return html;
    };

    const handleDownloadPDF = () => {
        if (!lessonPlan) return;

        const pdfHTML = generatePDFHTML(lessonPlan);
        const blob = new Blob([pdfHTML], { type: 'text/html' });
        const url = URL.createObjectURL(blob);

        const opt = {
            margin: [0.5, 0.5, 0.5, 0.5],
            filename: `Lesson Plan - ${grade}.pdf`, //`lesson_plan_${selectedSubject?.subject_name || 'subject'}_grade${grade}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                scrollX: 0,
                scrollY: 0
            },
            jsPDF: {
                unit: 'in',
                format: 'a4',
                orientation: 'portrait',
                compress: true
            },
            pagebreak: { mode: 'avoid-all' }
        };

        // Create a temporary iframe to render the HTML
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.left = '-9999px';
        iframe.style.top = '-9999px';
        iframe.style.width = '800px';
        iframe.style.height = '600px';
        document.body.appendChild(iframe);

        iframe.onload = () => {
            setTimeout(() => {
                html2pdf().set(opt).from(iframe.contentDocument.body).save().then(() => {
                    document.body.removeChild(iframe);
                    URL.revokeObjectURL(url);
                });
            }, 500);
        };

        iframe.src = url;
    };

    const generateWordDocument = async (lessonPlanData) => {
        // Enhanced theme colors for professional Word document
        const primaryColor = '546A2F'; // Theme main color
        const secondaryColor = 'A8B87C'; // Theme secondary color
        const successColor = '2e7d32'; // Material-UI success green
        const infoColor = '0288d1'; // Material-UI info blue
        const warningColor = 'ed6c02'; // Material-UI warning orange
        const errorColor = 'd32f2f'; // Material-UI error red
        const greyColor = '757575'; // Grey for subtle text

        // Enhanced section color mapping to match UI
        const getSectionColor = (sectionKey) => {
            const colorMap = {
                'learning_targets': successColor,
                'curriculum_alignment': infoColor,
                'opening': warningColor,
                'core_activity': primaryColor,
                'closure': secondaryColor,
                'assessment': errorColor,
                'materials': infoColor,
                'differentiation': successColor,
                'type': primaryColor
            };
            return colorMap[sectionKey] || primaryColor;
        };

        const doc = new Document({
            sections: [
                {
                    properties: {},
                    children: [
                        // Professional Header Section
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "📚 LESSON PLAN",
                                    bold: true,
                                    size: 48,
                                    color: primaryColor
                                })
                            ],
                            heading: HeadingLevel.TITLE,
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 200 }
                        }),

                        // Subject and Grade Badges
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: `📖 ${selectedSubject?.subject_name || 'Subject'}  •  🎓 Grade ${grade}`,
                                    bold: true,
                                    size: 24,
                                    color: secondaryColor
                                })
                            ],
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 600 }
                        }),

                        // Lesson Overview Section Header
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "📋 LESSON OVERVIEW",
                                    bold: true,
                                    size: 32,
                                    color: primaryColor
                                })
                            ],
                            heading: HeadingLevel.HEADING_1,
                            spacing: { before: 400, after: 300 }
                        }),

                        // Topic
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Topic: ",
                                    bold: true,
                                    size: 24,
                                    color: primaryColor
                                }),
                                new TextRun({
                                    text: `🎯 ${lessonPlanData.topic || 'N/A'}`,
                                    size: 24,
                                    color: successColor
                                })
                            ],
                            spacing: { after: 200 }
                        }),

                        // Duration
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Duration: ",
                                    bold: true,
                                    size: 24,
                                    color: primaryColor
                                }),
                                new TextRun({
                                    text: `⏰ ${lessonPlanData.duration || 'N/A'}`,
                                    size: 24,
                                    color: successColor
                                })
                            ],
                            spacing: { after: 500 }
                        }),

                        // Lesson Plan Information Section
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "LESSON PLAN INFORMATION",
                                    bold: true,
                                    size: 28,
                                    color: primaryColor
                                })
                            ],
                            heading: HeadingLevel.HEADING_1,
                            spacing: { before: 300, after: 200 }
                        }),

                        // Subject
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Subject: ",
                                    bold: true,
                                    size: 22,
                                    color: primaryColor
                                }),
                                new TextRun({
                                    text: selectedSubject?.subject_name || 'N/A',
                                    size: 22
                                })
                            ],
                            spacing: { after: 100 }
                        }),

                        // Grade
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Grade: ",
                                    bold: true,
                                    size: 22,
                                    color: primaryColor
                                }),
                                new TextRun({
                                    text: grade,
                                    size: 22
                                })
                            ],
                            spacing: { after: 100 }
                        }),

                        // Topic
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Topic: ",
                                    bold: true,
                                    size: 22,
                                    color: primaryColor
                                }),
                                new TextRun({
                                    text: topic,
                                    size: 22
                                })
                            ],
                            spacing: { after: 400 }
                        }),

                        // Enhanced Lesson Plan Content Sections
                        ...Object.entries(lessonPlanData)
                            .filter(([key]) => !['subject', 'grade', 'topic', 'duration'].includes(key))
                            .flatMap(([key, value]) => {
                                const sectionColor = getSectionColor(key);
                                const getSectionEmoji = (sectionKey) => {
                                    const emojiMap = {
                                        'learning_targets': '🎯',
                                        'curriculum_alignment': '📚',
                                        'opening': '🚀',
                                        'core_activity': '🔬',
                                        'closure': '🔄',
                                        'assessment': '📊',
                                        'materials': '🧰',
                                        'differentiation': '🎭',
                                        'type': '📋'
                                    };
                                    return emojiMap[sectionKey] || '📝';
                                };

                                return [
                                    // Section Header with Emoji and Color
                                    new Paragraph({
                                        children: [
                                            new TextRun({
                                                text: `${getSectionEmoji(key)} ${key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`,
                                                bold: true,
                                                size: 32,
                                                color: sectionColor
                                            })
                                        ],
                                        heading: HeadingLevel.HEADING_1,
                                        spacing: { before: 600, after: 300 }
                                    }),

                                    // Enhanced Content Formatting
                                    ...(typeof value === 'string' ? [
                                        new Paragraph({
                                            children: [
                                                new TextRun({
                                                    text: value,
                                                    size: 24,
                                                    color: '333333'
                                                })
                                            ],
                                            spacing: { after: 400, before: 100 }
                                        })
                                    ] : Array.isArray(value) ?
                                        value.map((item, index) => {
                                            if (typeof item === 'string') {
                                                return [
                                                    new Paragraph({
                                                        children: [
                                                            new TextRun({
                                                                text: `${index + 1}. `,
                                                                bold: true,
                                                                size: 20,
                                                                color: primaryColor
                                                            }),
                                                            new TextRun({
                                                                text: item,
                                                                size: 22,
                                                                color: '333333'
                                                            })
                                                        ],
                                                        spacing: { after: 150, before: 50 }
                                                    })
                                                ];
                                            } else if (typeof item === 'object' && item !== null) {
                                                // Enhanced object handling
                                                return Object.entries(item).flatMap(([subKey, subValue]) => [
                                                    new Paragraph({
                                                        children: [
                                                            new TextRun({
                                                                text: `• ${subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:`,
                                                                bold: true,
                                                                size: 26,
                                                                color: sectionColor
                                                            })
                                                        ],
                                                        spacing: { before: 300, after: 150 }
                                                    }),
                                                    ...(Array.isArray(subValue) ?
                                                        subValue.map((subItem, subIndex) =>
                                                            new Paragraph({
                                                                children: [
                                                                    new TextRun({
                                                                        text: `   ${subIndex + 1}. `,
                                                                        bold: true,
                                                                        size: 20,
                                                                        color: sectionColor
                                                                    }),
                                                                    new TextRun({
                                                                        text: subItem,
                                                                        size: 22,
                                                                        color: '333333'
                                                                    })
                                                                ],
                                                                spacing: { after: 100 }
                                                            })
                                                        ) : [
                                                            new Paragraph({
                                                                children: [
                                                                    new TextRun({
                                                                        text: `   ${String(subValue)}`,
                                                                        size: 22,
                                                                        color: '333333'
                                                                    })
                                                                ],
                                                                spacing: { after: 150 }
                                                            })
                                                        ]
                                                    )
                                                ]).flat();
                                            }
                                            return [new Paragraph({
                                                children: [
                                                    new TextRun({
                                                        text: `${index + 1}. ${String(item)}`,
                                                        size: 22,
                                                        color: '333333'
                                                    })
                                                ],
                                                spacing: { after: 150 }
                                            })];
                                        }).flat() :
                                        typeof value === 'object' && value !== null ?
                                            Object.entries(value).flatMap(([subKey, subValue]) => [
                                                new Paragraph({
                                                    children: [
                                                        new TextRun({
                                                            text: `• ${subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:`,
                                                            bold: true,
                                                            size: 26,
                                                            color: sectionColor
                                                        })
                                                    ],
                                                    spacing: { before: 300, after: 150 }
                                                }),
                                                ...(Array.isArray(subValue) ?
                                                    subValue.map((item, index) =>
                                                        new Paragraph({
                                                            children: [
                                                                new TextRun({
                                                                    text: `   ${index + 1}. `,
                                                                    bold: true,
                                                                    size: 20,
                                                                    color: sectionColor
                                                                }),
                                                                new TextRun({
                                                                    text: item,
                                                                    size: 22,
                                                                    color: '333333'
                                                                })
                                                            ],
                                                            spacing: { after: 100 }
                                                        })
                                                    ) : [
                                                        new Paragraph({
                                                            children: [
                                                                new TextRun({
                                                                    text: `   ${String(subValue)}`,
                                                                    size: 22,
                                                                    color: '333333'
                                                                })
                                                            ],
                                                            spacing: { after: 150 }
                                                        })
                                                    ]
                                                )
                                            ]) : [
                                                new Paragraph({
                                                    children: [
                                                        new TextRun({
                                                            text: String(value),
                                                            size: 24,
                                                            color: '333333'
                                                        })
                                                    ],
                                                    spacing: { after: 400 }
                                                })
                                            ]
                                    )
                                ];
                            }),

                        // Professional Footer
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "✨ Generated with Gradely Educational Platform",
                                    size: 20,
                                    italics: true,
                                    color: greyColor,
                                    bold: true
                                })
                            ],
                            alignment: AlignmentType.CENTER,
                            spacing: { before: 800, after: 200 }
                        }),

                        // Footer line separator
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "────────────────────────────────────────",
                                    size: 16,
                                    color: secondaryColor
                                })
                            ],
                            alignment: AlignmentType.CENTER,
                            spacing: { before: 100 }
                        })
                    ]
                }
            ]
        });

        return doc;
    };

    const downloadWordDocument = async (lessonPlanData) => {
        try {
            const doc = await generateWordDocument(lessonPlanData);
            const blob = await Packer.toBlob(doc);

            const element = document.createElement('a');
            element.href = URL.createObjectURL(blob);
            element.download = `Unit_Lesson_Plan_${selectedSubject?.subject_name || 'Subject'}_Grade${grade}.docx`;
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
            URL.revokeObjectURL(element.href);
        } catch (error) {
            console.error('Error generating Word document:', error);
            alert('Error generating Word document. Please try again.');
        }
    };

    const renderLessonPlan = (plan) => {
        if (!plan) return null;

        return (
            <Fade in timeout={600}>
                <Box ref={lessonPlanRef}>
                    {/* Professional Header Section */}
                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            color: 'white',
                            p: { xs: 3, sm: 4 },
                            borderRadius: { xs: 2, sm: 3 },
                            mb: 4,
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: '100%',
                                height: '100%',
                                background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="6" cy="6" r="6"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                                opacity: 0.1
                            }
                        }}
                    >
                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                            <Typography
                                variant={isSmallMobile ? "h5" : "h4"}
                                fontWeight="bold"
                                sx={{
                                    textAlign: 'center',
                                    mb: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                                }}
                            >
                                <MenuBookIcon sx={{ fontSize: 'inherit' }} />
                                📚 Lesson Plan
                            </Typography>

                            {/* Subject & Grade Badge */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: 2,
                                    flexWrap: 'wrap'
                                }}
                            >
                                <Box
                                    sx={{
                                        background: 'rgba(255,255,255,0.2)',
                                        borderRadius: 20,
                                        px: 3,
                                        py: 1,
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.3)'
                                    }}
                                >
                                    <Typography variant="body1" fontWeight="medium">
                                        📖 {plan.subject || 'N/A'}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        background: 'rgba(255,255,255,0.2)',
                                        borderRadius: 20,
                                        px: 3,
                                        py: 1,
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.3)'
                                    }}
                                >
                                    <Typography variant="body1" fontWeight="medium">
                                        🎓 Grade {plan.grade || 'N/A'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Topic and Duration Overview */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12}>
                            <Paper
                                elevation={3}
                                sx={{
                                    p: { xs: 3, sm: 4 },
                                    borderRadius: { xs: 2, sm: 3 },
                                    background: `linear-gradient(45deg, ${theme.palette.secondary.light}15 0%, ${theme.palette.primary.light}15 100%)`,
                                    border: `2px solid ${theme.palette.primary.light}`,
                                    position: 'relative'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <EmojiObjectsIcon sx={{ color: theme.palette.primary.main, mr: 1, fontSize: 24 }} />
                                    <Typography variant="h6" fontWeight="bold" color="primary">
                                        📋 Lesson Overview
                                    </Typography>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={8}>
                                        <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" gutterBottom>
                                            Topic:
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem', lineHeight: 1.6 }}>
                                            🎯 {plan.topic || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" gutterBottom>
                                            Duration:
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                background: theme.palette.success.light,
                                                color: theme.palette.success.contrastText,
                                                px: 2,
                                                py: 1,
                                                borderRadius: 2,
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            ⏰ {plan.duration || 'N/A'}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Detailed Content Sections */}
                    {Object.entries(plan).filter(([key]) => !['subject', 'grade', 'topic', 'duration'].includes(key)).map(([key, value], index) => {
                        // Get emoji and color for each section
                        const getSectionData = (sectionKey) => {
                            const sectionMap = {
                                'learning_targets': { emoji: '🎯', color: theme.palette.success.main, bgColor: theme.palette.success.light + '20' },
                                'curriculum_alignment': { emoji: '📚', color: theme.palette.info.main, bgColor: theme.palette.info.light + '20' },
                                'opening': { emoji: '🚀', color: theme.palette.warning.main, bgColor: theme.palette.warning.light + '20' },
                                'core_activity': { emoji: '🔬', color: theme.palette.primary.main, bgColor: theme.palette.primary.light + '20' },
                                'closure': { emoji: '🔄', color: theme.palette.secondary.main, bgColor: theme.palette.secondary.light + '20' },
                                'assessment': { emoji: '📊', color: theme.palette.error.main, bgColor: theme.palette.error.light + '20' },
                                'materials': { emoji: '🧰', color: theme.palette.info.main, bgColor: theme.palette.info.light + '20' },
                                'differentiation': { emoji: '🎭', color: theme.palette.success.main, bgColor: theme.palette.success.light + '20' },
                                'type': { emoji: '📋', color: theme.palette.primary.main, bgColor: theme.palette.primary.light + '20' }
                            };
                            return sectionMap[sectionKey] || { emoji: '📝', color: theme.palette.primary.main, bgColor: theme.palette.primary.light + '20' };
                        };

                        const sectionData = getSectionData(key);

                        return (
                            <Zoom in timeout={600 + (index + 4) * 100} key={key}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        mb: 3,
                                        borderRadius: { xs: 2, sm: 3 },
                                        overflow: 'hidden',
                                        border: `1px solid ${sectionData.color}30`,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            boxShadow: `0 8px 25px ${sectionData.color}20`,
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    {/* Section Header */}
                                    <Box
                                        sx={{
                                            background: sectionData.bgColor,
                                            borderBottom: `2px solid ${sectionData.color}30`,
                                            p: { xs: 2, sm: 3 }
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{
                                                color: sectionData.color,
                                                textTransform: 'capitalize',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    background: sectionData.color,
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    width: 32,
                                                    height: 32,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '1rem'
                                                }}
                                            >
                                                {sectionData.emoji}
                                            </Box>
                                            {key.replace(/_/g, ' ')}
                                        </Typography>
                                    </Box>

                                    {/* Section Content */}
                                    <Box sx={{ p: { xs: 2, sm: 3 } }}>
                                        {typeof value === 'string' ? (
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    lineHeight: 1.7,
                                                    fontSize: { xs: '0.95rem', sm: '1rem' },
                                                    color: theme.palette.text.primary,
                                                    whiteSpace: 'pre-line'
                                                }}
                                            >
                                                {value}
                                            </Typography>
                                        ) : Array.isArray(value) ? (
                                            <Box sx={{ ml: 1 }}>
                                                {value.map((item, idx) => (
                                                    <Box
                                                        key={idx}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            mb: 1.5,
                                                            '&:last-child': { mb: 0 }
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                background: sectionData.color,
                                                                color: 'white',
                                                                borderRadius: '50%',
                                                                minWidth: 20,
                                                                height: 20,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 'bold',
                                                                mr: 2,
                                                                mt: 0.2
                                                            }}
                                                        >
                                                            {idx + 1}
                                                        </Box>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                flex: 1,
                                                                lineHeight: 1.6,
                                                                fontSize: { xs: '0.95rem', sm: '1rem' }
                                                            }}
                                                        >
                                                            {typeof item === 'string' ? item : JSON.stringify(item)}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : typeof value === 'object' && value !== null ? (
                                            <Box>
                                                {Object.entries(value).map(([subKey, subValue], subIdx) => (
                                                    <Box key={subKey} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
                                                        <Typography
                                                            variant="subtitle2"
                                                            fontWeight="bold"
                                                            sx={{
                                                                color: sectionData.color,
                                                                mb: 1,
                                                                textTransform: 'capitalize',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: 6,
                                                                    height: 6,
                                                                    borderRadius: '50%',
                                                                    background: sectionData.color
                                                                }}
                                                            />
                                                            {subKey.replace(/_/g, ' ')}:
                                                        </Typography>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                ml: 2,
                                                                lineHeight: 1.6,
                                                                fontSize: { xs: '0.95rem', sm: '1rem' }
                                                            }}
                                                        >
                                                            {typeof subValue === 'string' ? subValue : JSON.stringify(subValue)}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    lineHeight: 1.7,
                                                    fontSize: { xs: '0.95rem', sm: '1rem' }
                                                }}
                                            >
                                                {String(value)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Paper>
                            </Zoom>
                        );
                    })}

                    {/* Professional Footer */}
                    <Paper
                        elevation={2}
                        sx={{
                            mt: 4,
                            p: 3,
                            borderRadius: 3,
                            background: `linear-gradient(45deg, ${theme.palette.grey[100]} 0%, ${theme.palette.grey[50]} 100%)`,
                            border: `1px solid ${theme.palette.divider}`,
                            textAlign: 'center'
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                fontStyle: 'italic',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1
                            }}
                        >
                            ✨ Generated with Gradely Educational Platform
                        </Typography>
                    </Paper>
                </Box>
            </Fade>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 } }}>
            <Fade in timeout={800}>
                <Box>
                    <Box sx={{ textAlign: 'center', mb: 2, mt: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                            <AssignmentIcon sx={{ fontSize: '2rem', color: theme.palette.primary.main }} />
                            <Typography
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                    fontSize: '2rem'
                                }}
                            >
                                Lesson Plan Generator
                            </Typography>
                        </Box>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    <Paper
                        elevation={3}
                        sx={{
                            p: { xs: 2, sm: 3, md: 4 },
                            borderRadius: { xs: 1.5, sm: 2, md: 3 },
                            background: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`
                        }}
                    >
                        <Stack spacing={3}>
                            <TextField
                                fullWidth
                                label="Subject"
                                value={selectedSubject}
                                onChange={(e) => setSelectedSubject(e.target.value)}
                                placeholder="Enter subject name"
                                InputProps={{
                                    startAdornment: <SchoolIcon sx={{ mr: 1, color: 'action.active' }} />
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: { xs: 1, sm: 1.5, md: 2 }
                                    }
                                }}
                            />

                            <FormControl fullWidth>
                                <InputLabel>Grade</InputLabel>
                                <Select
                                    value={grade}
                                    label="Grade"
                                    onChange={(e) => setGrade(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: { xs: 1, sm: 1.5, md: 2 }
                                        }
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <GradeIcon fontSize="small" />
                                            Select a grade
                                        </Box>
                                    </MenuItem>
                                    {gradeLevels.map((gradeLevel) => (
                                        <MenuItem key={gradeLevel} value={gradeLevel}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <GradeIcon fontSize="small" />
                                                Grade {gradeLevel}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                label="Topic"
                                fullWidth
                                value={topic}
                                onChange={(e) => setTopic(e.target.value)}
                                placeholder="e.g., Fractions on a Number Line"
                                multiline
                                minRows={3}
                                InputProps={{
                                    startAdornment: <EmojiObjectsIcon sx={{ mr: 1, color: 'action.active', mt: 1 }} />
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: { xs: 1, sm: 1.5, md: 2 }
                                    }
                                }}
                            />

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleGenerate}
                                disabled={loading}
                                size="large"
                                sx={{
                                    py: 1.5,
                                    borderRadius: { xs: 1, sm: 1.5, md: 2 },
                                    fontSize: '1.1rem',
                                    fontWeight: 'bold',
                                    backgroundColor: theme.palette.primary.main,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                        transform: 'translateY(-2px)',
                                        boxShadow: 4
                                    }
                                }}
                            >
                                {loading ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CircularProgress size={20} color="inherit" />
                                        <span>Generating...</span>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <GenerateIcon />
                                        <span>Generate Lesson Plan</span>
                                    </Stack>
                                )}
                            </Button>
                        </Stack>
                    </Paper>

                    {lessonPlan && (
                        <Box sx={{ mt: 4 }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                mb: 3,
                                flexDirection: isSmallMobile ? 'column' : 'row',
                                gap: 2
                            }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleDownloadPDF}
                                    sx={{
                                        borderRadius: { xs: 1, sm: 1.5, md: 2 },
                                        backgroundColor: theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark,
                                            transform: 'translateY(-2px)',
                                            boxShadow: 4
                                        }
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <DownloadIcon />
                                        <span>Download PDF</span>
                                    </Stack>
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => downloadWordDocument(lessonPlan)}
                                    sx={{
                                        borderRadius: 2,
                                        backgroundColor: theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark,
                                            transform: 'translateY(-2px)',
                                            boxShadow: 4
                                        }
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <DownloadIcon />
                                        <span>Download Word</span>
                                    </Stack>
                                </Button>
                            </Box>

                            <Paper
                                elevation={4}
                                sx={{
                                    p: { xs: 2, sm: 3, md: 4 },
                                    borderRadius: { xs: 1.5, sm: 2, md: 3 },
                                    overflowY: 'auto',
                                    background: theme.palette.background.default,
                                    border: `1px solid ${theme.palette.divider}`,
                                    '@media print': {
                                        borderRadius: 0,
                                        boxShadow: 'none',
                                        margin: 0,
                                        padding: '20px',
                                        maxHeight: 'none',
                                        overflowY: 'visible',
                                        pageBreakInside: 'avoid',
                                        breakInside: 'avoid'
                                    }
                                }}
                            >
                                {renderLessonPlan(lessonPlan)}
                            </Paper>
                        </Box>
                    )}

                    <Snackbar
                        open={!!error}
                        autoHideDuration={6000}
                        onClose={() => setError('')}
                    >
                        <Alert severity="error" onClose={() => setError('')}>
                            {error}
                        </Alert>
                    </Snackbar>
                </Box>
            </Fade>

            {/* Loading State */}
            {loading && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(8px)",
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            borderRadius: 4,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            backgroundColor: "white",
                            maxWidth: 400,
                            mx: 2
                        }}
                    >
                        <FloatingGradelyLoader
                            size={60}
                            showText={true}
                            text="Creating detailed lesson plan for your class. Please wait 1-2 minutes while your content is loading..."
                        />
                    </Paper>
                </Box>
            )}
        </Container>
    );
};

export default UnitLessonPlanGenerator;
